import { useId } from 'react';
import CampoPeriodo from '@/components/base/CampoPeriodo';
import type { PeriodoParcial } from '@/lib/periodo';

// Aviso de vencimento (componente 3.2 da especificação).
//
// Dois campos que sempre andam juntos: com quanta antecedência avisar, e quanto
// tempo o documento costuma levar para ser emitido. O segundo existe porque o
// primeiro sozinho não basta — avisar 30 dias antes de vencer um alvará que a
// prefeitura leva 60 dias para emitir é avisar tarde.
//
// ─── O disparo é Fase 2 ──────────────────────────────────────────────
//
// A especificação (secao 9) pede o **modelo de dados** pronto e deixa o envio
// de e-mail/notificação para depois. O componente diz isso na tela em vez de
// deixar a pessoa supor que preencher aqui já faz alguém ser avisado — que é
// como um campo de configuração vira uma promessa quebrada.

interface Props {
  /** Nulo significa "usa a janela global de /admin/notificações". */
  diasAntecedencia: number | null;
  onChangeDias: (dias: number | null) => void;
  tempoMedioGeracao: PeriodoParcial;
  onChangeTempoMedio: (periodo: PeriodoParcial) => void;
  /** Quantos dias a configuração global usa hoje, para o campo mostrar o padrão. */
  diasPadraoGlobal?: number | null;
  desabilitado?: boolean;
}

const CLASSE =
  'w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 disabled:opacity-60';

export default function CampoAvisoVencimento({
  diasAntecedencia,
  onChangeDias,
  tempoMedioGeracao,
  onChangeTempoMedio,
  diasPadraoGlobal,
  desabilitado = false,
}: Props) {
  const id = useId();

  const foraDaFaixa = diasAntecedencia !== null && (diasAntecedencia < 0 || diasAntecedencia > 365);

  return (
    <fieldset className="border border-background-300/60 rounded-md p-4">
      <legend className="px-1.5 text-sm font-medium text-foreground-700">
        Aviso de vencimento
      </legend>

      <p className="text-xs text-foreground-500 mb-3">
        Modelo pronto para a Fase 2. Hoje estes valores são guardados e ainda não disparam e-mail
        nem notificação.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor={id} className="block text-sm font-medium text-foreground-700 mb-1.5">
            Antecedência do aviso (dias)
          </label>
          <input
            id={id}
            type="text"
            inputMode="numeric"
            value={diasAntecedencia ?? ''}
            onChange={(e) => {
              const limpo = e.target.value.replace(/\D/g, '');
              onChangeDias(limpo === '' ? null : Number(limpo));
            }}
            disabled={desabilitado}
            placeholder={
              diasPadraoGlobal !== null && diasPadraoGlobal !== undefined
                ? `Padrão: ${diasPadraoGlobal}`
                : 'Padrão da plataforma'
            }
            aria-invalid={foraDaFaixa ? true : undefined}
            aria-describedby={`${id}-ajuda`}
            className={`${CLASSE} ${foraDaFaixa ? 'border-red-400' : ''}`}
          />
          <p id={`${id}-ajuda`} className="text-xs text-foreground-500 mt-1">
            {foraDaFaixa ? (
              <span className="text-red-600">Informe entre 0 e 365 dias.</span>
            ) : (
              <>
                Em branco: usa a janela definida em{' '}
                <span className="font-medium">Notificações</span>, que vale para toda a plataforma.
                Preencha só quando este caso precisar de mais antecedência que o resto.
              </>
            )}
          </p>
        </div>

        <CampoPeriodo
          rotulo="Tempo médio de emissão"
          valor={tempoMedioGeracao}
          onChange={onChangeTempoMedio}
          desabilitado={desabilitado}
          ajuda="Quanto o órgão costuma levar entre o pedido e o documento na mão."
          textoVazio="não entra no cálculo do aviso."
        />
      </div>
    </fieldset>
  );
}
